import { motion } from 'framer-motion'
import { Plus, CalendarDays } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { normalizeOpeningHourMode, type OpeningHour } from '@/lib/types/openingHour'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { fadeInUp } from '@/lib/motion'

interface WeekGridProps {
  hours: OpeningHour[]
  onSlotClick: (day: number) => void
  onEditHour: (hour: OpeningHour) => void
  onAddSpecial: () => void
}

function formatHHMM(val: number): string {
  const h = Math.floor(val / 100)
  const m = val % 100
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function HourCard({ h, onClick }: { h: OpeningHour; onClick: () => void }) {
  const schedule = normalizeOpeningHourMode(h.mode)
  const badgeVariant =
    schedule === 'booking' ? 'amber' : schedule === 'order' ? 'blue' : 'green'

  return (
    <button
      onClick={onClick}
      className="w-full p-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] hover:bg-[var(--color-bg-overlay)] transition-colors text-left cursor-pointer"
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Badge variant={badgeVariant}>{schedule}</Badge>
      </div>
      <p className="text-xs text-[var(--color-text-primary)] font-medium">
        {formatHHMM(h.open)} — {formatHHMM(h.close)}
      </p>
      {h.maxBookings > 0 && (
        <p className="text-[10px] text-[var(--color-text-muted)]">Max {h.maxBookings} bookings</p>
      )}
      {h.seatingActive && h.seatingInterval && (
        <p className="text-[10px] text-[var(--color-text-muted)]">
          Slots every {h.seatingInterval}min · {h.seatingDuration}min
        </p>
      )}
    </button>
  )
}

export function WeekGrid({ hours, onSlotClick, onEditHour, onAddSpecial }: WeekGridProps) {
  const { t } = useTranslation()

  const DAY_KEYS = [
    'openingHours.days.monday',
    'openingHours.days.tuesday',
    'openingHours.days.wednesday',
    'openingHours.days.thursday',
    'openingHours.days.friday',
    'openingHours.days.saturday',
    'openingHours.days.sunday',
  ] as const

  const regularHours = hours.filter((h) => !h.special)
  const specialHours = hours.filter((h) => h.special).sort((a, b) => {
    if (!a.endDate) return 1
    if (!b.endDate) return -1
    return a.endDate.localeCompare(b.endDate)
  })

  return (
    <div className="space-y-6">
      {/* Weekly schedule */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {DAY_KEYS.map((dayKey, idx) => {
          const dayNum = idx + 1
          const dayHours = regularHours.filter((h) => h.day === dayNum)
          return (
            <motion.div
              key={dayKey}
              {...fadeInUp}
              className="rounded-[var(--radius-lg)] bg-[var(--color-bg-surface)] border border-[var(--color-border-subtle)] p-3"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{t(dayKey)}</h3>
                <Button variant="ghost" size="icon-sm" onClick={() => onSlotClick(dayNum)}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              {dayHours.length === 0 ? (
                <p className="text-xs text-[var(--color-text-muted)] text-center py-4">{t('openingHours.closed')}</p>
              ) : (
                <div className="space-y-2">
                  {dayHours.map((h) => (
                    <HourCard key={h.id} h={h} onClick={() => onEditHour(h)} />
                  ))}
                </div>
              )}
            </motion.div>
          )
        })}
      </div>

      {/* Special date overrides */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">{t('openingHours.specialDates')}</h2>
            <p className="text-xs text-[var(--color-text-muted)]">{t('openingHours.specialDatesHint')}</p>
          </div>
          <Button variant="secondary" size="sm" onClick={onAddSpecial}>
            <Plus className="h-3.5 w-3.5" /> {t('openingHours.addSpecialDate')}
          </Button>
        </div>

        {specialHours.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 rounded-[var(--radius-lg)] border border-dashed border-[var(--color-border-subtle)]">
            <CalendarDays className="h-8 w-8 text-[var(--color-text-muted)] mb-2" />
            <p className="text-sm text-[var(--color-text-muted)]">{t('openingHours.noSpecialDates')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {specialHours.map((h) => {
              const dateLabel = h.endDate
                ? format(parseISO(h.endDate), 'EEE, MMM d yyyy')
                : 'Unknown date'
              return (
                <motion.div key={h.id} {...fadeInUp}>
                  <button
                    onClick={() => onEditHour(h)}
                    className="w-full p-3 rounded-[var(--radius-lg)] bg-[var(--color-bg-surface)] border border-[var(--color-border-subtle)] hover:border-[var(--color-border-default)] transition-colors text-left cursor-pointer"
                  >
                    <p className="text-sm font-medium text-[var(--color-text-primary)] mb-1">{dateLabel}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">
                      {formatHHMM(h.open)} — {formatHHMM(h.close)}
                    </p>
                    {h.maxBookings > 0 && (
                      <p className="text-[10px] text-[var(--color-text-muted)] mt-0.5">Max {h.maxBookings} bookings</p>
                    )}
                  </button>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
